import { useMemo } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { getTheme } from '@/theme';
import { AuthProvider, useAuth } from '@/hooks/useAuth';
import { ColorModeProvider, useColorMode } from '@/contexts/ColorModeContext';
import { LogoProvider } from '@/contexts/LogoProvider';
import { ProtectedRoute, LoginRedirect } from '@/components/ProtectedRoute';
import DashboardPage from '@/pages/DashboardPage';
import SsoPage from '@/pages/SsoPage';
import ReviewListPage from '@/pages/ReviewListPage';
import ReviewDetailPage from '@/pages/ReviewDetailPage';
import LiveListPage from '@/pages/LiveListPage';
import LiveDetailPage from '@/pages/LiveDetailPage';
import GroupsPage from '@/pages/GroupsPage';
import GroupDetailPage from '@/pages/GroupDetailPage';
import ScoreboardListPage from '@/pages/ScoreboardListPage';
import ScoreboardConsolePage from '@/pages/ScoreboardConsolePage';
import ScoreboardDisplayPage from '@/pages/ScoreboardDisplayPage';
import RouteDocumentTitle from '@/components/RouteDocumentTitle';
import DocumentFavicon from '@/components/DocumentFavicon';
import { PageDocumentTitleProvider } from '@/contexts/PageDocumentTitleContext';

function ThemedApp() {
  const { resolvedMode } = useColorMode();
  const { user } = useAuth();
  const theme = useMemo(
    () => getTheme(user?.role, resolvedMode),
    [user?.role, resolvedMode]
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline enableColorScheme />
      <BrowserRouter>
        <PageDocumentTitleProvider>
        <RouteDocumentTitle />
        <DocumentFavicon />
        <Routes>
          <Route path="/sso" element={<SsoPage />} />
          <Route path="/login" element={<LoginRedirect />} />
          <Route element={<ProtectedRoute />}>
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/review" element={<ReviewListPage />} />
            <Route path="/review/new" element={<Navigate to="/review?new=1" replace />} />
            <Route path="/review/:id" element={<ReviewDetailPage />} />
            <Route path="/live" element={<LiveListPage />} />
            <Route path="/live/new" element={<Navigate to="/live?new=1" replace />} />
            <Route path="/live/:id" element={<LiveDetailPage />} />
            <Route path="/groups" element={<GroupsPage />} />
            <Route path="/groups/:id" element={<GroupDetailPage />} />
            <Route path="/scoreboard" element={<ScoreboardListPage />} />
            <Route path="/scoreboard/:id" element={<ScoreboardConsolePage />} />
            <Route path="/scoreboard/:id/display" element={<ScoreboardDisplayPage />} />
          </Route>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
        </PageDocumentTitleProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default function App() {
  return (
    <ColorModeProvider>
      <LogoProvider>
        <AuthProvider>
          <ThemedApp />
        </AuthProvider>
      </LogoProvider>
    </ColorModeProvider>
  );
}
